import React, { useContext, useEffect, useMemo, useState } from "react";
import { BellRingSVG, EmptyBox } from "../../../../../app/assets/icons";
import { Badge, Checkbox, Spin } from "antd";
import { Popover } from "@material-ui/core";
import NotificationCheckbox from "./NotificationCheckbox";
import InfiniteScroll from "react-infinite-scroll-component";
import { httpService } from "../../../../../app/base/services/httpService.service";
import { useNotificationRedirect } from "../notificationContext/NotificationRedirect";
import { useNotificationStore } from "./useNotificationStore";
import { NotificationContext } from "../notificationContext/NotificationContext";
import { useTranslation } from "../../../../../app/i18n/LanguageContext";
import { formatRelativeDate } from "../../../../../app/base/utils/dateFormat";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../../../app/base/services/authentication.service";
import { UserRolesEnum } from "../../../../../app/pages/auth/userRoles.enum";
import {
   getNotificationBody,
   useCompletedByNames
} from "../../../../../app/pages/notifications/completedBy";
import {
   NotificationTypeIcon,
   getNotificationRoleColor,
   getNotificationTitle
} from "../../../../../app/pages/notifications/NotificationTypeIcon";

const NotificationPopover = ({
   notifications,
   notificationMetaData,
   user,
   toggleBell,
   handleCloseBell,
   // markAllNotificationsAsRead,
   // loadingMarkAllRead,
   // loadingMarkOneRead
   setPopoverUnreadCount,
   setPopoverUnreadCountAll,
   setPopoverIncCount
}) => {
   const { t } = useTranslation();
   const {
      notificationData,
      setNotificationData,
      newNotification,
      setNewNotification
   } = useNotificationStore();
   const completedByNames = useCompletedByNames(notificationData);
   const roleColor = useMemo(getNotificationRoleColor, []);
   const { unreadCount, handleToggleBell } = useContext(NotificationContext);

   const [hasMore, setHasMore] = useState(true);
   const [page, setPage] = useState(2);
   const [dataLoading, setDataLoading] = useState(false);
   const [activeTab, setActiveTab] = useState(1);
   const [pageCount, setPageCount] = useState(0);
   const [lastFetchedItems, setLastFetchedItems] = useState(0);

   const [loadingMarkAllRead, setLoadingMarkAllRead] = useState(false);
   // const [loadingMarkOneRead, setLoadingMarkOneRead] = useState({});
   const [loadingOneRead, setLoadingOneRead] = useState([]);

   const notificationRedirect = useNotificationRedirect();
   const navigate = useNavigate();

   // The Notifications page has no sidebar entry; this pop-up is the only way in.
   // SuperAdmin routes are mounted under "/admin" and approver routes under "/approver".
   const openNotificationsPage = () => {
      handleCloseBell();
      if (AuthService.isAuthorized([UserRolesEnum.SuperAdmin])) navigate("/admin/notifications");
      else if (AuthService.isAuthorized([UserRolesEnum.Approver])) navigate("/approver/notifications");
      else navigate("/notifications");
   };

   // Read notifications open their page too; only unread ones are marked read.
   const redirectNotification = (notification) => {
      if (!notification?.isRead) markRead(notification?.id);
      handleCloseBell();
      notificationRedirect(
         notification?.notificationType,
         notification?.entityId,
         notification?.workspaceId,
         notification?.notificationBody
      );
   };

   const markRead = async (id) => {
      // setLoadingMarkOneRead((prev) => ({
      //    ...prev,
      //    [id]: true
      // }))
      setLoadingOneRead((prev) => [...prev, id]);

      await httpService.post(
         "/Notifications/read",
         { id: id },
         () => {
            setNotificationData(
               notificationData?.map((notification) =>
                  notification?.id === id ? { ...notification, isRead: true } : notification
               )
            );
            setPopoverUnreadCount();
            // setLoadingMarkOneRead((prev) => ({
            //    ...prev,
            //    [id]: false
            // }));
            setLoadingOneRead((prev) => [...prev.filter((e) => e !== id)]);
         },
         (err) => {
            console.error(err);
            // setLoadingMarkOneRead((prev) => ({
            //    ...prev,
            //    [id]: false
            // }));
            setLoadingOneRead((prev) => [...prev.filter((e) => e !== id)]);
         }
      );
   };

   const markAllRead = () => {
      setLoadingMarkAllRead(true);

      httpService.post(
         "/Notifications/read/all",
         { userId: user?.id },
         () => {
            setNotificationData(
               notificationData?.map((notification) => ({ ...notification, isRead: true }))
            );
            setPopoverUnreadCountAll();
            setLoadingMarkAllRead(false);
         },
         (err) => {
            console.log(err);
         }
      );
   };

   useEffect(() => {
      setNotificationData(notifications);
      setLastFetchedItems(notifications?.length);
   }, [notifications]);

   const fetchNextData = () => {
      setDataLoading(true);
      httpService.get(
         `/Notifications/all/${user?.id}?pageNumber=${page}&pageSize=10&unRead=${
            activeTab === 1 ? false : activeTab === 2 ? true : false
         }`,
         (res) => {
            setDataLoading(false);
            setHasMore(page < res?.metaData?.pageCount);
            const responseData = res?.data;
            if (page === 1) {
               setNotificationData(responseData);
            } else {
               setNotificationData([...notificationData, ...responseData]);
            }
            setPageCount(res?.metaData?.pageNumber % res?.metaData?.totalItemCount);
            setLastFetchedItems(res?.data?.length);
            setPage(page + 1);
         },
         (err) => {
            console.log(err);
            setDataLoading(false);
         }
      );
   };

   const changeNotifType = (nr) => {
      setActiveTab(nr);
      setPage(1);
      setNotificationData([]);
      // fetchNextData();
   };

   useEffect(() => {
      if (page === 1) {
         fetchNextData();
      }
   }, [page]);

   useEffect(() => {
      if (newNotification) {
         let pushNew = {
            id: newNotification?.id,
            notificationTitle: newNotification?.notificationTitle,
            notificationBody: newNotification?.notificationBody,
            notificationType: newNotification?.notificationType,
            isRead: false,
            workspaceId: newNotification?.workspaceId,
            entityId: newNotification?.entityId,
            received: true
         };
         let notifiationLastRemoved = notificationData.slice(0, -1);
         if (lastFetchedItems < 10) {
            setNotificationData([pushNew, ...notificationData]);
         } else {
            setNotificationData([pushNew, ...notifiationLastRemoved]);
         }
         setHasMore(lastFetchedItems + 1 >= 10);
         setLastFetchedItems(lastFetchedItems + 1);
         setPopoverIncCount();
         setNewNotification(false);
      }
   }, [newNotification]);

   return (
      <>
         {" "}
         <Badge count={unreadCount}>
            <div onClick={handleToggleBell} className="bell">
               <img
                  src={BellRingSVG}
                  alt={t("notifications-tooltip-text")}
                  aria-controls="popover"
                  aria-haspopup="true"
                  data-toggle="modal"
                  data-target="#kt_chat_modal"
               />
            </div>
         </Badge>
         <Popover
            className="arrow-menu"
            id="popover"
            open={Boolean(toggleBell)}
            anchorEl={toggleBell}
            onClose={handleCloseBell}
            style={{
               position: "fixed",
               top: "50px",
               maxHeight: "350px",
               right: "40px"
               // maxWidth: "400px"
            }}
            PaperProps={{
               style: {
                  borderRadius: "8px"
               }
            }}
         >
            <div
               style={{
                  overflow: "hidden",
                  borderRadius: "10px",
                  boxShadow: "4px 8px 16px 0px #00266A33"
               }}
            >
               <div
                  className="d-flex"
                  style={{
                     gap: "30px",
                     alignItems: "center",
                     padding: "20px",
                     paddingBottom: "10px",
                     justifyContent: "space-between",
                     borderBottom: "1px solid #D9D9D9"
                  }}
               >
                  <p
                     style={{
                        fontWeight: 500,
                        fontSize: "18px",
                        fontFamily: "poppins",
                        color: "#3A3737",
                        margin: 0
                     }}
                  >
                     {t("notifications-tooltip-text")}
                  </p>
                  <div className="d-flex" style={{ gap: "10px", alignItems: "center" }}>
                     <div
                        className={`mark-all-read ${
                           notificationData?.every((notification) => notification?.isRead)
                              ? "disabled"
                              : ""
                        }`}
                        onClick={
                           !notificationData?.every((notification) => notification?.isRead) &&
                           markAllRead
                        }
                     >
                        {loadingMarkAllRead ? (
                           <Spin />
                        ) : (
                           <NotificationCheckbox
                              checked={
                                 notifications?.length > 0 &&
                                 notifications?.every((notification) => notification?.isRead)
                              }
                              small={true}
                              disabled={notifications?.every(
                                 (notification) => notification?.isRead
                              )}
                           />
                        )}
                        <span
                           style={{
                              color: notifications?.length === 0 ? "#BFBFBF" : "#5492FF"
                           }}
                        >
                           {t("mark-all-as-read-button")}
                        </span>
                     </div>
                  </div>
               </div>
               <div
                  id="notificationTarget"
                  style={{ overflowY: "auto", maxHeight: "300px", backgroundColor: "#fff" }}
               >
                  <>
                     <div className="notificationType">
                        <div
                           className={`notificationTypeWrapper ${activeTab === 1 && "active"}`}
                           onClick={() => changeNotifType(1)}
                        >
                           <span>{t("all-tab-label")}</span>
                        </div>
                        <div
                           className={`notificationTypeWrapper ${activeTab === 2 && "active"}`}
                           onClick={() => changeNotifType(2)}
                        >
                           <span>{t("unread-tab-label")}</span>
                        </div>
                     </div>
                     {notificationData?.length !== 0 ? (
                        <InfiniteScroll
                           dataLength={notificationData?.length}
                           hasMore={hasMore}
                           next={fetchNextData}
                           loader={<Spin style={{ width: "100%", margin: "15px auto" }} />}
                           scrollableTarget="notificationTarget"
                        >
                           {notificationData?.map((notification) => (
                              <div
                                 className={`single-notification-item ${notification?.received &&
                                    "new-notification"}`}
                                 key={notification?.id}
                                 style={{
                                    borderBottom: "1px solid #D9D9D9",
                                    cursor: "pointer"
                                 }}
                                 onClick={() => redirectNotification(notification)}
                              >
                                 <div
                                    className="single-notification"
                                    style={{
                                       padding: "12px 20px",
                                       transition: "0.2s",
                                       backgroundColor: notification?.isRead && "#F7F7F7"
                                    }}
                                 >
                                    <div
                                       className="d-flex"
                                       style={{
                                          gap: "30px",
                                          alignItems: "flex-start",
                                          justifyContent: "space-between"
                                       }}
                                    >
                                       <div
                                          className="d-flex"
                                          style={{ gap: "12px", alignItems: "flex-start", minWidth: 0 }}
                                       >
                                       <span
                                          aria-hidden="true"
                                          style={{
                                             display: "inline-flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                             flexShrink: 0,
                                             width: "36px",
                                             height: "36px",
                                             borderRadius: "50%",
                                             // Same 8% role-colour tint as the Notifications page.
                                             color: roleColor,
                                             backgroundColor: `${roleColor}14`
                                          }}
                                       >
                                          <NotificationTypeIcon type={notification?.notificationType} />
                                       </span>
                                       <div className="d-flex flex-column">
                                          <span
                                             style={{
                                                fontSize: "16px",
                                                color: "#3A3737",
                                                margin: 0,
                                                wordBreak: "break-word",
                                                fontWeight: 500
                                             }}
                                          >
                                             {getNotificationTitle(notification, t)}
                                          </span>
                                          <span
                                             style={{
                                                fontSize: "14px",
                                                color: "#9E9FA2",
                                                margin: 0,
                                                wordBreak: "break-word",
                                                fontWeight: 400
                                             }}
                                          >
                                             {getNotificationBody(notification, completedByNames, t)}
                                          </span>
                                       </div>
                                       </div>

                                       <div className="checkbox-comp" style={{ marginTop: "5px" }}>
                                          {!notification?.isRead ? (
                                             loadingOneRead.includes(notification?.id) ||
                                             loadingMarkAllRead ? (
                                                <Spin />
                                             ) : (
                                                <NotificationCheckbox />
                                             )
                                          ) : (
                                             <NotificationCheckbox checked={true} disabled={true} />
                                          )}
                                       </div>
                                    </div>
                                    <span
                                       style={{
                                          fontSize: "12px",
                                          fontWeight: 500,
                                          color: "#979797"
                                       }}
                                    >
                                       {formatRelativeDate(notification?.createdDate)}
                                    </span>
                                 </div>
                              </div>
                           ))}
                        </InfiniteScroll>
                     ) : (
                        <>
                           {dataLoading ? (
                              <Spin style={{ width: "100%", margin: "15px auto" }} />
                           ) : (
                              <div
                                 style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "20px",
                                    borderTop: "1px solid #DFE6FF"
                                 }}
                              >
                                 <img src={EmptyBox} alt="" width={80} height={50} />
                                 <p
                                    style={{
                                       margin: 0,
                                       fontWeight: 500,
                                       fontSize: "16px",
                                       color: "#3A3737"
                                    }}
                                 >
                                    {t("no-notifications-title-text")}
                                 </p>
                                 <p style={{ margin: 0, fontSize: "14px", color: "#979797" }}>
                                    {t("up-to-date-description-text")}
                                 </p>
                              </div>
                           )}
                        </>
                     )}
                  </>
               </div>
               <div
                  role="button"
                  tabIndex={0}
                  onClick={openNotificationsPage}
                  onKeyDown={(e) => e.key === "Enter" && openNotificationsPage()}
                  style={{
                     padding: "12px 20px",
                     textAlign: "center",
                     borderTop: "1px solid #D9D9D9",
                     backgroundColor: "#fff",
                     color: "#5492FF",
                     fontWeight: 500,
                     fontSize: "14px",
                     cursor: "pointer"
                  }}
               >
                  {t("view-all-notifications-button")}
               </div>
            </div>
         </Popover>
      </>
   );
};

export default NotificationPopover;
